/**
 * officeFile.ts
 *
 * Reads the active Office document's bytes via the Office.js Common API
 * `getFileAsync(Compressed)`. Handles the three things every caller needs:
 *   - Sequential slice reading (Office.js can't read slices in parallel)
 *   - Retry on transient "internal error" responses
 *   - A hard timeout so callers never hang forever
 *
 * Shared by the Word (.docx) and PowerPoint (.pptx) readers so the slice-reading
 * engine lives in one place; `noun`/`label` only shape the user-facing message
 * text and retry telemetry (AIVA-7885).
 *
 * NOTE: like getFileAsync everywhere, this returns the LAST SAVED version of the
 * file. An unsaved document reads as empty/stale, so callers should treat a
 * failed/empty read as "nothing to attach" rather than an error.
 */

import { logOfficeError } from "../telemetry";

const SLICE_SIZE = 4 * 1024 * 1024; // 4 MB — Office reads the file in slices.
const MAX_RETRIES = 3;
const DEFAULT_TIMEOUT_MS = 15000;

export interface ReadActiveFileOptions {
  /** Lowercase noun for the user-facing error text, e.g. "document" / "presentation". */
  readonly noun: string;
  /** Label for the timeout message and retry telemetry (usually the caller name). */
  readonly label: string;
  /** Overall read timeout in ms; defaults to 15s. */
  readonly timeoutMs?: number;
}

function officeGetFileAsync(): Promise<Office.File> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: SLICE_SIZE },
      (result: Office.AsyncResult<Office.File>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error?.message ?? "unknown"));
          return;
        }
        resolve(result.value);
      },
    );
  });
}

function officeGetSliceAsync(file: Office.File, idx: number): Promise<Office.Slice> {
  return new Promise((resolve, reject) => {
    file.getSliceAsync(idx, (sr) => {
      if (sr.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Slice read failed: ${sr.error?.message ?? "?"}`));
        return;
      }
      resolve(sr.value);
    });
  });
}

function officeCloseAsync(file: Office.File): Promise<void> {
  return new Promise((resolve) => {
    file.closeAsync(() => resolve());
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

async function drainSlices(file: Office.File, noun: string): Promise<Uint8Array> {
  const sliceCount = file.sliceCount;
  if (sliceCount === 0) {
    await officeCloseAsync(file);
    throw new Error(`${capitalize(noun)} appears empty — save the file (Ctrl+S) and try again.`);
  }

  const slices: Uint8Array[] = new Array(sliceCount);
  try {
    for (let idx = 0; idx < sliceCount; idx++) {
      const slice = await officeGetSliceAsync(file, idx); // NOSONAR file slices are read in order
      const raw = slice.data as Uint8Array | number[];
      slices[idx] = new Uint8Array(raw);
    }
  } finally {
    await officeCloseAsync(file);
  }

  const total = slices.reduce((s, sl) => s + sl.byteLength, 0);
  const combined = new Uint8Array(total);
  let off = 0;
  for (const sl of slices) {
    combined.set(sl, off);
    off += sl.byteLength;
  }
  return combined;
}

async function fetchWithRetry(noun: string, label: string): Promise<Uint8Array> {
  if (typeof Office === "undefined" || !Office?.context?.document) {
    throw new Error("Office context not available — is the add-in still attached?");
  }

  for (let attemptsLeft = MAX_RETRIES; ; attemptsLeft--) {
    try {
      const file = await officeGetFileAsync();
      return await drainSlices(file, noun); // NOSONAR retry attempts must run one after another
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const transient = msg.toLowerCase().includes("internal");
      if (attemptsLeft > 0 && transient) {
        const backoff = (MAX_RETRIES - attemptsLeft + 1) * 400;
        logOfficeError(err, `${label}-retry`);
        await delay(backoff); // NOSONAR retry attempts must run one after another
        continue;
      }
      throw new Error(`Could not read the ${noun}: ${msg}. Try saving (Ctrl+S) and clicking again.`);
    }
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`${label} timed out after ${ms / 1000}s. Try saving (Ctrl+S) and reopening the panel.`));
    }, ms);
    promise.then(
      (val) => { if (!settled) { settled = true; clearTimeout(timer); resolve(val); } },
      (err) => { if (!settled) { settled = true; clearTimeout(timer); reject(err); } },
    );
  });
}

/** The active Office document's current (last-saved) bytes. */
export function readActiveOfficeFileBytes(opts: ReadActiveFileOptions): Promise<Uint8Array> {
  return withTimeout(
    fetchWithRetry(opts.noun, opts.label),
    opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    opts.label,
  );
}
